import { type AIAgentExecutionPayload } from '../../../store/ai/aiAgent'
import { type AIAgentExecutionResult } from '../../../models/AIAgent'
import { type AccountOpeningPrefill } from '../../../models/AccountOpeningDescriptor'
import { partySchemaRole } from './parties'
import { withoutBlanks } from './schemaValues'
import { type WizardAccount, type WizardContext, type WizardParty } from './types'

export const accountPrefillSources = (ctx: WizardContext, account: WizardAccount, schema: Record<string, unknown>): Record<string, unknown> => ({
  schema,
  accountTypeId: account.typeId,
  roomId: ctx.roomId,
  sessionId: ctx.sessionId,
  meetingTemplateId: ctx.meetingTemplateId,
  contactId: ctx.contactId,
})

export const partyPrefillSources = (ctx: WizardContext, party: WizardParty, schema: Record<string, unknown>): Record<string, unknown> => ({
  schema,
  role: partySchemaRole(party),
  who: party.name,
  roomId: ctx.roomId,
  sessionId: ctx.sessionId,
  meetingTemplateId: ctx.meetingTemplateId,
  contactId: party.contactId,
})

const buildPrefillContext = (keys: string[], sources: Record<string, unknown>): Record<string, unknown> => {
  const context: Record<string, unknown> = {}
  keys.forEach((key) => {
    const value = sources[key]
    if (value === null || value === undefined || value === '') {
      return
    }
    context[key] = value
  })
  return context
}

const prefillPayload = (prefill: AccountOpeningPrefill, sources: Record<string, unknown>): AIAgentExecutionPayload => ({
  type: prefill.agent,
  context: buildPrefillContext(prefill.context, sources),
})

export interface PrefillTarget {
  key: string
  sources: Record<string, unknown>
}

export const fetchPrefilled = async ({
  executePrefill,
  accountId,
  prefill,
  targets,
}: {
  executePrefill: (args: { accountId: string; payload: AIAgentExecutionPayload }) => { unwrap: () => Promise<unknown> }
  accountId: string
  prefill: AccountOpeningPrefill
  targets: PrefillTarget[]
}): Promise<{ filled: Map<string, Record<string, unknown>>; failedKeys: string[] }> => {
  const filled = new Map<string, Record<string, unknown>>()
  const failedKeys: string[] = []
  await Promise.all(
    targets.map(async (target) => {
      try {
        const response = (await executePrefill({ accountId, payload: prefillPayload(prefill, target.sources) }).unwrap()) as AIAgentExecutionResult<
          Record<string, unknown>
        >
        filled.set(target.key, withoutBlanks(response.result))
      } catch {
        failedKeys.push(target.key)
      }
    }),
  )
  return { filled, failedKeys }
}
